<script setup lang="ts">
import { onMounted, ref, shallowRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTenantPermissionDefinitionsStore } from '@/@vxapp/TenantPermissionDefinitions/useTenantPermissionDefinitionsStore'
import type {
  TenantPermissionDefinition,
  TenantPermissionDefinitionWritable,
} from '@/@vxapp/TenantPermissionDefinitions/useTenantPermissionDefinitionsStore'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { Subject } from '@/@vxapp/@Enums/PHP/Auth/Ability/Subjects/DataProviders/AbilitySubject'
import type { AbilityAction } from '@/@vxapp/@Enums/PHP/Auth/DataProviders/AbilityAction'
import type { AbilitySubject } from '@/@vxapp/@Enums/PHP/Auth/Ability/Subjects/DataProviders/AbilitySubject'
import { A } from '@/@vxapp/@Enums/PHP/Auth/DataProviders/AbilityAction'
import { canPageMeta } from '@/@layouts/plugins/casl'
import { cloneReactiveData } from '@/@core/utils/cloneReactiveData'
import { useAccessContext, inaccessible } from '@/access/useAccessContext'
import TenantPermissionDefinitionForm from '@/components/Forms/TenantPermissionDefinitions/TenantPermissionDefinitionForm.vue'

definePage({
  meta: { ...PageMeta.TenantPermissionDefinitions.List.Meta, pageTitle: 'Permission definitions' },
})

const store = useTenantPermissionDefinitionsStore()
const route = useRoute()
const router = useRouter()
const rows = shallowRef<TenantPermissionDefinition[]>([])
const selected = ref<Partial<TenantPermissionDefinition> | null>(null)
const dialog = ref<'create' | 'edit' | 'delete' | null>(null)
const error = ref('')
const errors = ref<unknown>()
const success = ref('')
const loading = ref(false)
const denied = ref(false)
const search = ref('')
const page = ref(1)
const totalPages = ref(1)
let request = 0
let selectionRequest = 0

const context = useAccessContext(
  () => {
    request += 1
    selectionRequest += 1
    rows.value = []
    selected.value = null
    dialog.value = null
    error.value = ''
    errors.value = undefined
    success.value = ''
    loading.value = false
    denied.value = false
    search.value = ''
    page.value = 1
    totalPages.value = 1
  },
  undefined,
  load
)

function allowed(action: AbilityAction, subject: AbilitySubject) {
  return canPageMeta({ action, subject })
}
async function load() {
  const epoch = context.epoch()
  const version = ++request

  loading.value = true
  error.value = ''
  denied.value = false

  const result = await store.fetchAll({
    page: page.value,
    limit: 25,
    search: search.value || undefined,
  })

  if (!context.isCurrent(epoch) || version !== request) return
  loading.value = false
  if (result.responseCode !== 200 || !('tenantPermissionDefinitions' in result)) {
    rows.value = []
    denied.value = inaccessible(result.responseCode)
    error.value = result.message || 'Permission definitions could not be loaded.'

    return
  }
  rows.value = result.tenantPermissionDefinitions
  totalPages.value = Number(store.paginationMeta?.totalPages ?? 1)
}
async function syncQuery() {
  await router.replace({
    query: {
      ...(page.value > 1 ? { page: String(page.value) } : {}),
      ...(search.value ? { search: search.value } : {}),
    },
  })
}
function restoreQuery() {
  const incoming = Number(route.query.page)

  page.value = Number.isInteger(incoming) && incoming > 0 ? incoming : 1
  search.value = typeof route.query.search === 'string' ? route.query.search : ''
}
async function searchRows() {
  page.value = 1
  await syncQuery()
  await load()
}
async function previous() {
  page.value -= 1
  await syncQuery()
  await load()
}
async function next() {
  page.value += 1
  await syncQuery()
  await load()
}
function cancel() {
  selectionRequest += 1
  dialog.value = null
  selected.value = null
  errors.value = undefined
  error.value = ''
}
async function create() {
  if (!allowed(A.CREATE, Subject.TENANT_PERMISSION_DEFINITIONS)) return
  cancel()

  const epoch = context.epoch()
  const version = ++selectionRequest
  const result = await store.fetchTemplate()
  if (!context.isCurrent(epoch) || version !== selectionRequest) return
  if (result.responseCode !== 200) {
    denied.value = inaccessible(result.responseCode)
    error.value = result.message

    return
  }
  selected.value = cloneReactiveData(store.template ?? {})
  dialog.value = 'create'
}
async function select(row: TenantPermissionDefinition, mode: 'edit' | 'delete') {
  cancel()

  const epoch = context.epoch()
  const version = ++selectionRequest
  const result = await store.fetch(row.permissionDefinitionGUID)
  if (!context.isCurrent(epoch) || version !== selectionRequest) return
  if (result.responseCode !== 200 || !('tenantPermissionDefinition' in result)) {
    denied.value = inaccessible(result.responseCode)
    if (denied.value) rows.value = []
    error.value = result.message

    return
  }
  selected.value = cloneReactiveData(result.tenantPermissionDefinition)
  dialog.value = mode
}
function edit(row: TenantPermissionDefinition) {
  if (allowed(A.UPDATE, Subject.TENANT_PERMISSION_DEFINITIONS_UPDATE_GUID)) void select(row, 'edit')
}
function remove(row: TenantPermissionDefinition) {
  if (allowed(A.DELETE, Subject.TENANT_PERMISSION_DEFINITIONS_DELETE_GUID))
    void select(row, 'delete')
}
function view(row: TenantPermissionDefinition) {
  void router.push({
    name: PageMeta.TenantPermissionDefinitions.Preview.Name,
    params: { guid: row.permissionDefinitionGUID },
  })
}
async function save(payload: TenantPermissionDefinitionWritable) {
  if (loading.value) return
  const mode = dialog.value
  const identifier = selected.value?.permissionDefinitionGUID
  if (mode !== 'create' && (mode !== 'edit' || !identifier)) return
  if (
    !allowed(
      mode === 'create' ? A.CREATE : A.UPDATE,
      mode === 'create'
        ? Subject.TENANT_PERMISSION_DEFINITIONS
        : Subject.TENANT_PERMISSION_DEFINITIONS_UPDATE_GUID
    )
  )
    return
  const epoch = context.epoch()

  loading.value = true

  const result =
    mode === 'create' ? await store.create(payload) : await store.update(identifier!, payload)

  if (!context.isCurrent(epoch)) return
  loading.value = false
  if (result.responseCode !== (mode === 'create' ? 201 : 200)) {
    error.value = result.message || 'The change could not be saved.'
    errors.value = result.errors
    if (inaccessible(result.responseCode)) {
      rows.value = []
      selected.value = null
      dialog.value = null
      denied.value = true
    }

    return
  }
  cancel()
  success.value = 'Saved successfully.'
  await load()
}
async function confirmDelete() {
  const identifier = selected.value?.permissionDefinitionGUID
  if (
    !identifier ||
    loading.value ||
    !allowed(A.DELETE, Subject.TENANT_PERMISSION_DEFINITIONS_DELETE_GUID)
  )
    return
  const epoch = context.epoch()

  loading.value = true

  const result = await store.delete(identifier)
  if (!context.isCurrent(epoch)) return
  loading.value = false
  if (result.responseCode !== 200) {
    error.value = result.message || 'Deletion failed.'
    if (inaccessible(result.responseCode)) {
      rows.value = []
      selected.value = null
      dialog.value = null
      denied.value = true
    }

    return
  }
  cancel()
  success.value = 'Deleted successfully.'
  await load()
}
restoreQuery()
onMounted(load)
watch(
  () => route.query,
  () => {
    const previousQuery = `${page.value}:${search.value}`

    restoreQuery()
    if (`${page.value}:${search.value}` !== previousQuery) void load()
  }
)
</script>

<template>
  <main data-testid="tenant-permission-definitions-workspace">
    <h1 class="mb-4">Permission definitions</h1>
    <VAlert
      v-if="denied"
      type="error"
      data-testid="tenant-context-unavailable"
      >This resource is inaccessible in the current context.</VAlert
    >
    <VAlert
      v-else-if="error"
      type="error"
      class="mb-4"
      >{{ error }}</VAlert
    >
    <VAlert
      v-if="success"
      type="success"
      class="mb-4"
      >{{ success }}</VAlert
    >
    <VCard>
      <VCardText>
        <VForm
          class="d-flex flex-wrap gap-2 mb-4"
          role="search"
          @submit.prevent="searchRows"
        >
          <AppTextField
            v-model="search"
            label="Search permission definitions"
            :rules="[]"
          />
          <VBtn
            type="submit"
            :disabled="loading"
            >Search</VBtn
          >
          <VBtn
            v-if="allowed(A.CREATE, Subject.TENANT_PERMISSION_DEFINITIONS)"
            :disabled="loading"
            @click="create"
            >Create definition</VBtn
          >
        </VForm>
        <VProgressLinear
          v-if="loading"
          indeterminate
          aria-label="Loading permission definitions"
        />
        <p v-if="!loading && !error && !denied && rows.length === 0">
          No permission definitions found.
        </p>
        <VTable
          v-if="rows.length"
          density="comfortable"
        >
          <thead>
            <tr>
              <th>Name</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in rows"
              :key="row.permissionDefinitionGUID"
              :data-testid="`tenant-permission-definition-row-${row.permissionDefinitionGUID}`"
            >
              <td>{{ row.name }}</td>
              <td>
                <div class="d-flex flex-wrap gap-1">
                  <VBtn
                    v-if="allowed(A.READ, Subject.TENANT_PERMISSION_DEFINITIONS_GUID)"
                    size="small"
                    @click="() => view(row)"
                    >View</VBtn
                  >
                  <VBtn
                    v-if="allowed(A.UPDATE, Subject.TENANT_PERMISSION_DEFINITIONS_UPDATE_GUID)"
                    size="small"
                    :disabled="loading"
                    @click="() => edit(row)"
                    >Edit</VBtn
                  >
                  <VBtn
                    v-if="allowed(A.DELETE, Subject.TENANT_PERMISSION_DEFINITIONS_DELETE_GUID)"
                    size="small"
                    color="error"
                    :disabled="loading"
                    @click="() => remove(row)"
                    >Delete</VBtn
                  >
                </div>
              </td>
            </tr>
          </tbody>
        </VTable>
        <div class="d-flex flex-wrap align-center gap-2 mt-4">
          <VBtn
            :disabled="loading || page <= 1"
            @click="previous"
            >Previous</VBtn
          ><span>Page {{ page }} of {{ totalPages }}</span
          ><VBtn
            :disabled="loading || page >= totalPages"
            @click="next"
            >Next</VBtn
          >
        </div>
      </VCardText>
    </VCard>
    <VDialog
      :model-value="dialog !== null"
      max-width="700"
      persistent
    >
      <VCard v-if="selected">
        <VCardTitle>{{
          dialog === 'delete'
            ? 'Delete definition?'
            : dialog === 'create'
              ? 'Create definition'
              : 'Edit definition'
        }}</VCardTitle>
        <VCardText>
          <VAlert
            v-if="error"
            type="error"
            class="mb-4"
            >{{ error }}</VAlert
          >
          <TenantPermissionDefinitionForm
            v-if="dialog !== 'delete'"
            :definition="selected"
            :is-edit="dialog === 'edit'"
            :loading="loading"
            :errors="errors"
            @submit="save"
            @cancel="cancel"
          />
          <template v-else
            ><p>Delete this definition from the current tenant?</p>
            <VBtn
              color="error"
              :loading="loading"
              @click="confirmDelete"
              >Confirm delete</VBtn
            ><VBtn
              :disabled="loading"
              @click="cancel"
              >Cancel</VBtn
            ></template
          >
        </VCardText>
      </VCard>
    </VDialog>
  </main>
</template>
